import { useEffect, useState } from "react";
import { iosScanQRCode } from "../../../tools/ios/qrcode";
import { iosGenerateHaptic, isIOSApp } from "../../../tools/ios";
import { isAndroidApp } from "../../../tools/android";
import { HapticEffect } from "../../../interfaces";
import { androidScanQRCode } from "../../../tools/android/qrcode";
import { displayToast } from "../../../components/app/AppToast";
import { useTranslation } from "react-i18next";

const ALLOWED_HOSTS = [
  `${globalThis.location.origin}/`,
  "https://app.juisci.com/",
  "https://s.juisci.com/",
  "https://dev.juisci.com/",
  "https://s-staging.juisci.com/",
];

// Html5Qrcode library type
interface Html5QrcodeClass {
  new (elementId: string): Html5QrcodeInstance;
}

interface Html5QrcodeInstance {
  start: (
    cameraIdOrConfig: { facingMode: string },
    config: { fps: number; qrbox: { width: number; height: number } },
    onSuccess: (decodedText: string) => void,
    onError: () => void
  ) => Promise<void>;
  stop: () => Promise<void>;
}

// Type declaration for html5-qrcode on globalThis
interface QRCodeGlobal {
  Html5Qrcode?: Html5QrcodeClass;
}
const qrGlobal = globalThis as unknown as QRCodeGlobal;

// Function to dynamically load html5-qrcode from CDN
async function loadHtml5Qrcode(): Promise<Html5QrcodeClass> {
  if (qrGlobal.Html5Qrcode !== undefined) {
    return qrGlobal.Html5Qrcode;
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://cdn.jsdelivr.net/npm/html5-qrcode@2.3.8/html5-qrcode.min.js";
    script.async = true;
    script.addEventListener("load", () => {
      if (qrGlobal.Html5Qrcode === undefined) {
        reject(new Error("Html5Qrcode not available after script load"));
      } else {
        resolve(qrGlobal.Html5Qrcode);
      }
    });
    script.onerror = () => {
      reject(new Error("Failed to load html5-qrcode from CDN"));
    };
    document.head.append(script);
  });
}

export function useQRCodeScanner(launchOnMount: boolean = false) {
  const { t } = useTranslation();
  const [isError, setError] = useState(false);

  useEffect(() => {
    if (launchOnMount && (isIOSApp() || isAndroidApp())) {
      handleQRCodeScan();
    }
  }, []);

  function handleQRCodeScan() {
    if (isIOSApp()) {
      iosGenerateHaptic(HapticEffect.SELECTION);
      handleIOSQRCodeScan();
    } else if (isAndroidApp()) {
      handleAndroidQRCodeScan();
    } else {
      // Web fallback
      handleWebQRCodeScan();
    }
  }

  async function handleIOSQRCodeScan() {
    try {
      const result = await iosScanQRCode();
      redirectAfterScan(result.success, result.qrCode);
    } catch (error) {
      const errorText = String(error instanceof Error ? error.message : error);
      const isPermissionDenied =
        errorText.includes("NotAllowedError") ||
        errorText.includes("PermissionDeniedError") ||
        errorText.includes("Permission dismissed");
      if (isPermissionDenied) {
        console.warn("QR scanner: camera permission denied by user");
        displayToast(t("Allow camera access to scan QR codes."), "warning");
      } else {
        console.error("Failed to scan QR code:", error);
        displayToast(t("Couldn't scan the QR code."), "error");
      }
      setError(true);
    }
  }

  function handleAndroidQRCodeScan() {
    androidScanQRCode((success: boolean, qrCode: string) => {
      redirectAfterScan(success, qrCode);
    });
  }

  async function handleWebQRCodeScan() {
    let html5QrCode: Html5QrcodeInstance | null = null;
    let container: HTMLElement | null = null;

    const cleanup = async () => {
      if (html5QrCode) {
        try {
          await html5QrCode.stop();
        } catch {
          // Ignore stop errors
        }
      }
      if (container && container.parentNode) {
        container.remove();
      }
    };

    try {
      const Html5Qrcode = await loadHtml5Qrcode();

      // Remove any existing scanner container
      const existingContainer = document.querySelector("#qr-scanner-container");
      if (existingContainer) {
        existingContainer.remove();
      }

      // Create backdrop and bottom sheet container
      container = document.createElement("div");
      container.id = "qr-scanner-container";
      container.style.cssText =
        "position:fixed;top:0;left:0;width:100%;height:100%;z-index:9999;display:flex;flex-direction:column;justify-content:flex-end;";

      // Backdrop
      const backdrop = document.createElement("div");
      backdrop.style.cssText =
        "position:absolute;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);";
      backdrop.addEventListener("click", cleanup);
      container.append(backdrop);

      // Bottom sheet
      const sheet = document.createElement("div");
      sheet.style.cssText =
        "position:relative;background:#fff;border-radius:16px 16px 0 0;max-height:85dvh;display:flex;flex-direction:column;max-width:600px;width:100%;margin:0 auto;";

      // Header with close button
      const header = document.createElement("div");
      header.style.cssText =
        "display:flex;align-items:center;justify-content:space-between;padding:16px 22px;border-bottom:1px solid #DFE6EC;";
      const title = document.createElement("span");
      title.textContent = "Scan QR Code";
      title.style.cssText = "font-size:18px;font-weight:600;color:#212121;";
      const closeBtn = document.createElement("button");
      closeBtn.textContent = "✕";
      closeBtn.style.cssText =
        "background:none;border:none;font-size:24px;color:#212121;cursor:pointer;padding:4px 8px;";
      closeBtn.addEventListener("click", cleanup);
      header.append(title);
      header.append(closeBtn);
      sheet.append(header);

      // Scanner container
      const scannerDiv = document.createElement("div");
      scannerDiv.id = "reader";
      scannerDiv.style.cssText = "background:#000;min-height:400px;";
      sheet.append(scannerDiv);

      container.append(sheet);
      document.body.append(container);

      html5QrCode = new Html5Qrcode("reader");

      // Start scanning
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        async (decodedText: string) => {
          await cleanup();
          redirectAfterScan(true, decodedText);
        },
        () => {}
      );
    } catch (error) {
      await cleanup();
      const errorText = String(error instanceof Error ? error.message : error);
      const isPermissionDenied =
        errorText.includes("NotAllowedError") ||
        errorText.includes("PermissionDeniedError") ||
        errorText.includes("Permission dismissed");
      if (isPermissionDenied) {
        console.warn("QR scanner: camera permission denied by user");
        displayToast(t("Allow camera access to scan QR codes."), "warning");
      } else {
        console.error("Failed to scan QR code:", error);
        displayToast(t("Couldn't scan the QR code."), "error");
      }
      setError(true);
    }
  }

  function redirectAfterScan(success: boolean, qrCode: string) {
    if (!qrCode) {
      // User cancelled the scanner — silently signal error so the caller can
      // navigate back. No toast: cancellation is not an error.
      setError(true);
      return;
    }

    if (!success) {
      displayToast(t("Couldn't scan the QR code."), "error");
      setError(true);
      return;
    }

    if (!ALLOWED_HOSTS.some((host: string) => qrCode.startsWith(host))) {
      displayToast(t("This QR code isn't allowed here."), "error");
      setError(true);
      return;
    }

    globalThis.location.href = qrCode;
  }

  return { handleQRCodeScan, isError };
}
